import React, { useEffect, useRef, useState } from 'react';

import { DatasetDataInfoResponse } from 'api/model/dataset/DatasetDataInfoResponse';
import { DatasetDataInfo } from 'models/dataset-models';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { MultiSelect, MultiSelectChangeParams } from 'primereact/multiselect';
import { useScadaStore } from 'store/scada-store';
import CalendarWrapper from '../../basic/calendar-wrapper/CalendarWrapper';
import DatasetLineChart from '../charts/dataset-line-chart/DatasetLineChart';
import './Scada.scss';
import { ScadaTag } from './ScadaTag';
import { ParentRect, ScadaElementsProps, ScadaProps } from './scadaTypes';
import { cancelSelections, getScadaData, getScadaImageUrl, handleContextMenu, showCords } from './scadaUtils';
import { InputNumber } from 'primereact/inputnumber';

const defaultElementParameters: ParentRect = {
	width: 0,
	height: 0,
	top: 0,
	left: 0,
};

const defaultDateTime = {
	date: new Date(),
	manualTime: false,
};

export default function Scada(props: ScadaProps) {
	const [parent, setParent] = useState(defaultElementParameters);
	const [dialog, setDialog] = useState(false);
	const [selectedIdentifiers, setSelectedIdentifiers] = useState<string[]>([]);
	const [dateTime, setDateTime] = useState(defaultDateTime);
	const [layers, setLayers] = useState<string[]>([]);
	const [gridSize, setGridSize] = useState(0.5)
	const ref = useRef<HTMLDivElement>(null);
	const scadaStore = useScadaStore();

	const scada = scadaStore.scadaMap.get(props.id!);
	let timeout: NodeJS.Timeout | null = null;
	const layerOptions = scada?.labelLayers ?? [];

	useEffect(() => {
		if (!scada?.labels) {
			scadaStore.addScada({
				scadaId: props.id!,
				labels: props.elements ?? [],
				labelLayers: props.labelLayers ?? [],
			});
		} else {
			scadaStore.updateScadaLabels(props.id!, props.elements ?? []);
		}
	}, []);

	useEffect(() => {
		ref.current?.addEventListener('click', cancelSelections);
		ref.current?.addEventListener('mousemove', showCords);
		ref.current?.addEventListener('contextmenu', onContextMenu);

		const rect = ref.current!.getBoundingClientRect();
		setParent({
			width: rect.width,
			height: rect.height,
			top: rect.top,
			left: rect.left,
		});
		return () => {
			ref.current?.removeEventListener('click', cancelSelections);
			ref.current?.removeEventListener('mousemove', showCords);
			ref.current?.removeEventListener('contextmenu', onContextMenu);
		};
	}, [ref.current]);

	useEffect(() => {
		setLayers(layerOptions.filter(g => g.visible).map(g => g.layer));
	}, [layerOptions])

	const handleScroll = () => {
		if (timeout) {
			clearTimeout(timeout);
		}
		timeout = setTimeout(() => {
			const rect = ref.current!.getBoundingClientRect();
			setParent({
				width: rect.width,
				height: rect.height,
				top: rect.top,
				left: rect.left,
			});
		}, 200);
	};

	useEffect(() => {
		window.addEventListener('scroll', handleScroll);

		return () => window.removeEventListener('scroll', handleScroll);
	}, []);

	const onContextMenu = (e: MouseEvent) => {
		handleContextMenu(e, openDialog);
	};

	const openDialog = () => {
		setSelectedIdentifiers([
			...new Set(window.scada.selectedLabels.map(l => l.dataset.identifier)),
		]);
		setDialog(true);
	};

	const handleLayerMultiselect = (e: MultiSelectChangeParams) => {
		const layers = layerOptions.map(g => ({
			...g,
			visible: e.value.includes(g.layer),
		}));
		scadaStore.updateScadaLabelLayers(props.id!, layers);
		setLayers(e.value);
	};

	const elemProps: ScadaElementsProps = {
		...props,
		date: dateTime.manualTime ? dateTime.date : undefined,
		returnDate: e => setDateTime({ ...dateTime, date: e }),
		openDialogCallBack: openDialog,
		parent,
		layerOptions,
		gridSize,
	};

	return (
		<>
			<div id='tooltip-container'></div>
			<div className='flex flex-column gap-3'>
				<div className='flex justify-content-between'>
					{props.showScadaSettings && (
						<div className='flex gap-1'>
							<CalendarWrapper
								value={dateTime.date}
								onChange={e => setDateTime({ date: e, manualTime: true })}
							/>
							{dateTime.manualTime && (
								<Button
									icon='pi pi-sync'
									disabled={!dateTime.manualTime}
									onClick={() => setDateTime(defaultDateTime)}
								/>
							)}
						</div>
					)}
					<div className='flex gap-5 justify-content-end w-full'>
						{window.isEditMode && (
							<div className='flex gap-2 align-items-center'>
								<label htmlFor='gridSize'>Grid, %:</label>
								<InputNumber
									inputId='gridSize'
									value={gridSize}
									onChange={e => setGridSize(e.value ?? 1)}
									inputStyle={{ width: '3rem' }}
									maxFractionDigits={1}
								/>
							</div>
						)}

						{layerOptions.length > 1 && (
							<MultiSelect
								value={layers}
								onChange={handleLayerMultiselect}
								options={layerOptions.map(g => g.layer)}
								className='w-8rem'
								maxSelectedLabels={0}
								selectedItemsLabel='Layers'
								placeholder='Layers'
							/>
						)}
					</div>
				</div>
				<div ref={ref} className='scadaViewContainer relativePos'>
					<img
						alt='SCADA schematics'
						className='scadaView'
						src={getScadaImageUrl(props.scadaBackgroundId)}
					/>

					<ScadaElements {...elemProps} />
				</div>
			</div>

			<Dialog
				header='SCADA tags chart'
				visible={dialog}
				style={{ width: 'min(100vw, 1000px)', height: '500px' }}
				modal={false}
				resizable={true}
				draggable={true}
				maximizable
				onHide={() => setDialog(false)}>
				<DatasetLineChart
					identifiers={selectedIdentifiers}
					referenceTime={
						dateTime.manualTime ? dateTime.date.toISOString() : undefined
					}
					showDateSelection={true}
					dynamicHeight={true}
					chartDialogOpen={dialog}
				/>
			</Dialog>
		</>
	);
}

function ScadaElements(props: ScadaElementsProps) {
	const [dataMap, setDataMap] = useState<Map<String, DatasetDataInfo>>(
		new Map(),
	);

	useEffect(() => {
		setDataMap(new Map());
		loadData();

		const int = setInterval(loadData, (props.refreshRate ?? 60) * 1000);

		return () => {
			console.log('Cleaning up SCADA refresh interval');
			clearInterval(int);
		};
	}, [props.date]);

    const loadData = () => {
			const chunkSize = 16;
			const tmpMap = dataMap ?? new Map();
			let count = 0;
			const identifiers = props.elements?.map(e => e.identifier);
			if (!identifiers) return;

			while (count < identifiers.length) {
				const chunk = identifiers.slice(count, (count += chunkSize));

				getScadaData(chunk, props.date?.toISOString()).then(
					(data: Array<DatasetDataInfoResponse>) => {
						const currentDate = new Date();
						data.forEach(d => {
							tmpMap.set(d.identifier, {
								...d,
								loadedTime: props.date || currentDate,
							} as DatasetDataInfo);
						});

						if (!props.date) {
							props.returnDate(currentDate);
						}

						setDataMap(new Map(tmpMap));
					},
				);
			}
		};

    return (
			<>
				{[...(props.elements ?? [])].map(el => {
					const layer = props.layerOptions.find(g => g.layer === el.layer);
					if (layer && !layer.visible) return;
					const dataset = dataMap.get(el.identifier);

					return (
						<ScadaTag
							key={`${props.id}-${el.identifier}`}
							model={props}
							scadaId={props.id!}
							x={el.xCoord}
							y={el.yCoord}
							dataset={dataset}
							identifier={el.identifier}
							bgColor={el.bgColor}
							fontSize={el.fontSize}
							refTime={props.date}
							parent={props.parent}
							gridSize={props.gridSize}
						/>
					);
				})}
			</>
		);
}
